import { useMemo, useState, useEffect } from "react";
import { naytaPvm } from "./pvm";
import { luku } from "./luvut";
import {
  api, ApiError, maanantai, pvmTeksti, lisaaPaivia,
  type Osaamisluokka, type Patevyystyyppi,
  type MitoitusMonta, type MitoitusOsaaminenTulos, type MitoitusRivi,
} from "./api";

import { Ohje } from "./Ohje";
import { t, virheTeksti } from "./kieli";
/* ═══════════════════════════════════════════════════════════
   Mitoituskysely — "Voidaanko ottaa tämä urakka?"

   Useita osaamisia kerralla: kullekin oma henkilömäärä ja
   valinnaiset pätevyysvaatimukset. Kukin tarkistetaan omaa
   kapasiteettiaan vasten; koko urakan vastaus on HEIKOIN
   osaaminen/viikko. Vaatimukset rajaavat vapaat niihin, joilla
   JOKAINEN vaatimus on voimassa koko jakson (Toni 24.9.2026:
   yhden täyttävä ei kelpaa).

   Ei tallenna mitään — kysymys on voisimmeko, ei teemmekö.
   ═══════════════════════════════════════════════════════════ */

const TILA_JARJESTYS: Record<string, number> = {
  "ei riitä": 0, "vuokraamalla": 1, "täpärästi": 2, "riittää": 3,
};

type Kyselyrivi = {
  osaamisluokka_id: number | "";
  henkiloa: string;
  patevyystyyppi_idt: number[];
};

export default function Mitoitus() {
  const [luokat, setLuokat] = useState<Osaamisluokka[]>([]);
  const [patevyystyypit, setPatevyystyypit] = useState<Patevyystyyppi[]>([]);
  const [rivit, setRivit] = useState<Kyselyrivi[]>([
    { osaamisluokka_id: "", henkiloa: "2", patevyystyyppi_idt: [] },
  ]);
  const [alku, setAlku] = useState(() => pvmTeksti(maanantai(new Date())));
  const [loppu, setLoppu] = useState(() => pvmTeksti(lisaaPaivia(maanantai(new Date()), 27)));

  const [tulos, setTulos] = useState<MitoitusMonta | null>(null);
  const [ladataan, setLadataan] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  useEffect(() => {
    api.osaamisluokat().then(setLuokat).catch(() => setLuokat([]));
    api.patevyystyypit().then(setPatevyystyypit).catch(() => setPatevyystyypit([]));
  }, []);

  const paivita = (i: number, m: Partial<Kyselyrivi>) =>
    setRivit((rs) => rs.map((r, j) => (j === i ? { ...r, ...m } : r)));
  const lisaaRivi = () =>
    setRivit((rs) => [...rs, { osaamisluokka_id: "", henkiloa: "1", patevyystyyppi_idt: [] }]);
  const poistaRivi = (i: number) =>
    setRivit((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs));

  async function kysy(e: React.FormEvent) {
    e.preventDefault();
    const osaamiset = rivit
      .filter((r) => r.osaamisluokka_id !== "" && Number(r.henkiloa.replace(",", ".")) > 0)
      .map((r) => ({
        osaamisluokka_id: Number(r.osaamisluokka_id),
        henkiloa: Number(r.henkiloa.replace(",", ".")),
        patevyystyyppi_idt: r.patevyystyyppi_idt,
      }));
    if (osaamiset.length === 0) { setVirhe(t("mit.virhe.valitse")); return; }
    // ⛔ Ei pyöristetä: puolikas ihminen torjutaan nimetysti (Toni 23.9.). Palvelin
    // tekee saman tarkistuksen — lomake on apu, ei portti.
    const puolikas = osaamiset.find((o) => !Number.isInteger(o.henkiloa));
    if (puolikas) {
      setVirhe(t("mit.virhe.puolikas", { n: luku(puolikas.henkiloa) }));
      return;
    }
    if (loppu < alku) { setVirhe(t("mit.virhe.loppu_ennen")); return; }

    setLadataan(true); setVirhe(null);
    try { setTulos(await api.mitoitusMonta(alku, loppu, osaamiset)); }
    catch (err) { setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("mit.virhe.kysely")); setTulos(null); }
    finally { setLadataan(false); }
  }

  // Yhdistetty vastaus: heikoin osaaminen (heikoin viikko) ratkaisee.
  const yhteenveto = useMemo(() => {
    if (!tulos || tulos.osaamiset.length === 0) return null;
    let heikoin: { tila: string; osaaminen: string } | null = null;
    for (const o of tulos.osaamiset) {
      for (const r of o.rivit) {
        if (!heikoin || (TILA_JARJESTYS[r.tila] ?? 9) < (TILA_JARJESTYS[heikoin.tila] ?? 9)) {
          heikoin = { tila: r.tila, osaaminen: o.nimi };
        }
      }
    }
    return heikoin;
  }, [tulos]);

  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("valikko.mitoitus")}</div>
        <div className="head__rivi">
          <h1>{t("mit.otsikko")}</h1>
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{t("mit.ohje.kuka")}</>}
        luvut={<>{t("mit.ohje.luvut")}</>} />

      <section className="panel lomake">
        <div className="panel__head">
          <h2>{t("mit.kysely")}</h2>
          <span className="hint">{t("mit.kysely_hint")}</span>
        </div>
        {/* noValidate: selaimen oma step-tarkistus pysäyttäisi lähetyksen yleisellä
            kuplalla ennen kysy()-funktiota, eikä nimetty syy (puolikas ihminen) näkyisi. */}
        <form onSubmit={kysy} noValidate data-mitoitus="">
          <table className="mitoitus__kysely">
            <thead>
              <tr>
                <th>{t("mit.osaaminen")}</th>
                <th>{t("mit.henkiloa")}</th>
                {patevyystyypit.length > 0 && <th>{t("mit.vaatimukset_sarake")}</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rivit.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select value={r.osaamisluokka_id}
                            onChange={(e) => paivita(i, { osaamisluokka_id: e.target.value ? Number(e.target.value) : "" })}>
                      <option value="">{t("hl.valitse")}</option>
                      {luokat.map((l) => (
                        <option key={l.id} value={l.id}>{l.nimi} ({l.henkiloita})</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {/* ⚠ Tässä luki min 0,5 ja step 0,5 — puolikkaat olivat tarjottu valinta. */}
                    <input type="number" min={1} step={1} style={{ width: 70 }} data-henkiloa={i}
                           value={r.henkiloa} onChange={(e) => paivita(i, { henkiloa: e.target.value })} />
                  </td>
                  {patevyystyypit.length > 0 && (
                    <td data-vaatimukset={i}>
                      {r.patevyystyyppi_idt.map((id) => (
                        <span key={id} className="mitoitus__vaatimus" data-vaatimus={id}>
                          {patevyystyypit.find((p) => p.id === id)?.nimi ?? id}
                          <button type="button" className="mitoitus__poista" title={t("mit.poista_vaatimus")}
                                  onClick={() => paivita(i, { patevyystyyppi_idt: r.patevyystyyppi_idt.filter((x) => x !== id) })}>×</button>
                        </span>
                      ))}
                      {/* Valinta lisää vaatimuksen listaan ja palaa tyhjäksi; valittu ei tarjoudu uudelleen. */}
                      <select value="" data-lisaa-vaatimus={i}
                              onChange={(e) => e.target.value && paivita(i, {
                                patevyystyyppi_idt: [...r.patevyystyyppi_idt, Number(e.target.value)] })}>
                        <option value="">{r.patevyystyyppi_idt.length ? t("mit.lisaa_vaatimus") : t("mit.ei_vaatimusta")}</option>
                        {patevyystyypit.filter((p) => !r.patevyystyyppi_idt.includes(p.id))
                          .map((p) => <option key={p.id} value={p.id}>{p.nimi}</option>)}
                      </select>
                    </td>
                  )}
                  <td>
                    {rivit.length > 1 && (
                      <button type="button" className="mitoitus__poista" title={t("mit.poista_rivi")}
                              onClick={() => poistaRivi(i)}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="nappi nappi--kevyt mitoitus__lisaa" onClick={lisaaRivi}>
            {t("mit.lisaa_osaaminen")}
          </button>

          <div className="mitoitus__jakso">
            <label>{t("mit.alkaa")}
              <input type="date" value={alku} onChange={(e) => setAlku(e.target.value)} />
            </label>
            <label>{t("mit.paattyy")}
              <input type="date" value={loppu} onChange={(e) => setLoppu(e.target.value)} />
            </label>
            <button type="submit" className="nappi nappi--paa" disabled={ladataan}>
              {ladataan ? t("mit.lasketaan") : t("mit.riittaako")}
            </button>
          </div>
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
        </form>
      </section>

      {yhteenveto && (
        <section className={"vastaus vastaus--" + tilaLuokka(yhteenveto.tila)}>
          <div className="vastaus__otsikko">{otsikko(yhteenveto.tila)}</div>
          <p className="vastaus__teksti">
            {yhteenveto.tila === "riittää"
              ? t("mit.riittaa_kaikilla")
              : <>{t("mit.heikoin_alku")} <strong>{yhteenveto.osaaminen}</strong>{t("mit.heikoin_loppu")}</>}
          </p>
        </section>
      )}

      {tulos?.osaamiset.map((o) => (
        <OsaamisenTulos key={o.osaamisluokka_id + ":" + o.vaatimukset.map((v) => v.id).join(",")} o={o} />
      ))}

    </>
  );
}

/* Yhden osaamisen viikkotulos. */
function OsaamisenTulos({ o }: { o: MitoitusOsaaminenTulos }) {
  if (o.rivit.length === 0) {
    return (
      <section className="panel">
        <div className="panel__head"><h2>{o.nimi}</h2></div>
        <p className="dim">{t("mit.ei_tyopaivia")}</p>
      </section>
    );
  }
  return (
    <section className="panel scroll">
      <div className="panel__head">
        <h2>{o.nimi} <span className="dim">· {luku(o.henkiloa)} {t("mit.hlo")}</span></h2>
        <span className="hint">
          {o.vaatimukset.length
            ? t("mit.patevyys_hint", { v: o.vaatimukset.map((v) => v.nimi).join(" + ") }) + (o.vaatimukset.length > 1 ? t("mit.kaikki_vaaditaan") : "")
            : t("mit.heikoin_viikko")}
        </span>
      </div>
      {o.vaatimukset.length > 0 && (
        <p className={o.ilman_patevyytta > 0 ? "huom mitoitus__patevyysvihje" : "dim"} role="status"
           data-tayttaa-kaikki={o.tayttaa_kaikki} data-osaajia={o.osaajia}>
          <strong>{o.tayttaa_kaikki} / {o.osaajia}</strong> {t("mit.osaajaa_tayttaa")}
          {o.vaatimukset.length > 1 ? t("mit.kaikki_vaatimukset") : t("mit.vaatimuksen")}{t("mit.koko_jakson")}
          {o.ilman_patevyytta > 0 && <>
            {" "}{t("mit.puuttuu", { lista: o.vaatimukset.filter((v) => v.puuttuu > 0).map((v) => `${v.nimi} ${v.puuttuu}`).join(" · ") })}
            {" "}{t("mit.joko")} <strong>{t("mit.suoritettavissa")}</strong> {t("mit.tai_vain")} <strong>{t("mit.kirjaamatta")}</strong> {t("mit.jarjestelmaan")}
          </>}
        </p>
      )}
      <table className="tbl tbl--mitoitus">
        <thead>
          <tr>
            <th className="th--nimi">{t("eten.viikko")}</th>
            <th>{t("mit.tyopaivia")}</th><th>{t("mit.tarve")}</th><th>{t("mit.oma_vapaa")}</th><th>{t("mit.vuokravara")}</th>
            <th>{t("mit.paikkoja")}</th><th>{t("mit.ostettava")}</th><th>{t("mit.vaje")}</th><th>{t("mit.puskuri")}</th><th>{t("nippu.tila")}</th>
          </tr>
        </thead>
        <tbody>
          {o.rivit.map((r: MitoitusRivi) => (
            <tr key={r.viikko_alku} className={"tila--" + tilaLuokka(r.tila)}>
              <th className="th--nimi">
                <span className="lyh">{t("mit.vk", { n: r.viikko })}</span>
                <span className="kokonimi">{naytaPvm(r.viikko_alku)}</span>
              </th>
              <td className="mono">{r.tyopaivia}</td>
              <td className="mono">{luku(r.tarve)}</td>
              <td className="mono">{luku(r.vapaa_oma)}</td>
              <td className="mono">{luku(r.vapaa_vuokra)}</td>
              <td className="mono">{r.vuokrapaikkoja > 0 ? r.vuokrapaikkoja : ""}</td>
              <td className="mono">{r.alihankintatarve > 0 ? luku(r.alihankintatarve) : ""}</td>
              <td className="mono">{r.vaje > 0 ? luku(r.vaje) : ""}</td>
              <td className="mono">{r.puskuria}</td>
              <td><span className={"tilamerkki tilamerkki--" + tilaLuokka(r.tila)}>{tilaNimi(r.tila)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* ── apurit ─────────────────────────────────────────────── */



const tilaLuokka = (ti: string) =>
  ti === "ei riitä" ? "vanhentunut"
  : ti === "vuokraamalla" ? "kriittinen"
  : ti === "täpärästi" ? "varoitus"
  : "ok";

const otsikko = (ti: string) =>
  ti === "ei riitä" ? t("mit.otsikko.ei_riita")
  : ti === "vuokraamalla" ? t("mit.otsikko.vuokraamalla")
  : ti === "täpärästi" ? t("mit.otsikko.taparasti")
  : t("mit.otsikko.riittaa");

/** Palvelimen tilasana näytettäväksi; tuntematon arvo sellaisenaan. */
const tilaNimi = (ti: string) =>
  ti === "ei riitä" ? t("mit.tila.ei_riita")
  : ti === "vuokraamalla" ? t("mit.tila.vuokraamalla")
  : ti === "täpärästi" ? t("mit.tila.taparasti")
  : ti === "riittää" ? t("mit.tila.riittaa")
  : ti;
